import * as RadixSeparator from "@radix-ui/react-separator";
import * as RadixTooltip from "@radix-ui/react-tooltip";
import * as RadixVisuallyHidden from "@radix-ui/react-visually-hidden";
import type { ReactNode } from "react";
import { cx, TONE_CLASSES, type Tone } from "../styles";

/**
 * The small pieces: a rule, an avatar, a count badge, a tooltip, a key cap, and
 * the escape hatch for text only a screen reader should hear.
 */

export interface SeparatorProps {
	orientation?: "horizontal" | "vertical";
	className?: string;
}

/**
 * A rule between things.
 *
 * Radix's rather than an `<hr>` because it sets `role="none"` — a separator
 * that is purely visual should not be announced, and the default `<hr>` is. A
 * separator that genuinely divides sections can be given a label, and then it
 * is worth announcing.
 */
export function Separator({ orientation = "horizontal", className }: SeparatorProps) {
	return (
		<RadixSeparator.Root
			orientation={orientation}
			decorative
			className={cx(
				"shrink-0 bg-border",
				orientation === "horizontal" ? "h-px w-full" : "h-full w-px",
				className,
			)}
		/>
	);
}

export interface AvatarProps {
	/** The person's name, which the initials come from. */
	name: string;
	size?: number;
	className?: string;
}

/**
 * Initials in a brand circle.
 *
 * No image. The system has no avatar upload and inventing a placeholder that
 * looks like a missing photograph is worse than initials, which are at least
 * true. When EP-05 adds photographs this grows an `src`; the initials stay as
 * the fallback, because a photograph that fails to load must not leave a hole.
 *
 * Decorative to assistive technology: every place this appears has the person's
 * name in text beside it, and hearing "SA" before "Sara Aoun" is noise.
 */
export function Avatar({ name, size = 28, className }: AvatarProps) {
	return (
		<span
			aria-hidden="true"
			style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
			className={cx(
				"inline-grid shrink-0 place-items-center rounded-full bg-brand font-bold text-fg-on-brand",
				className,
			)}
		>
			{initialsOf(name)}
		</span>
	);
}

/**
 * Up to two initials.
 *
 * First and last word rather than the first two, so "Sara Al Aoun" is SA and
 * not "SA" from "Sara Al". Falls back to a single glyph for a one-word name,
 * and to nothing at all for an empty one — better an empty circle than a
 * literal "?" that looks like a rendering failure.
 */
export function initialsOf(name: string): string {
	const words = name.trim().split(/\s+/).filter(Boolean);
	if (words.length === 0) {
		return "";
	}
	const first = words[0] ?? "";
	const last = words.length > 1 ? (words[words.length - 1] ?? "") : "";
	return `${[...first][0] ?? ""}${[...last][0] ?? ""}`.toUpperCase();
}

export interface BadgeProps {
	children: ReactNode;
	/** `accent` for a count that wants attention, `neutral` for one that does not. */
	tone?: Extract<Tone, "neutral" | "brand"> | "accent";
	className?: string;
}

/**
 * A small count pill, for a tab or a nav item.
 *
 * Not a {@link StatusChip}: a badge carries a number, a chip carries a state.
 * The `accent` tone is the one place the yellow is used as a fill, and it
 * carries `--accent-fg` ink rather than white — yellow with white on it is
 * unreadable, which is why `--accent` is documented as never being a text
 * colour.
 */
export function Badge({ children, tone = "neutral", className }: BadgeProps) {
	const tones: Record<NonNullable<BadgeProps["tone"]>, string> = {
		neutral: TONE_CLASSES.neutral,
		brand: TONE_CLASSES.brand,
		accent: "bg-accent text-accent-fg border-accent",
	};

	return (
		<span
			className={cx(
				"inline-flex h-[18px] min-w-[18px] items-center justify-center rounded-full border px-1.5",
				"text-[11px] font-bold [font-variant-numeric:tabular-nums]",
				tones[tone],
				className,
			)}
		>
			{children}
		</span>
	);
}

export interface TooltipProps {
	/** The text. Short: a tooltip is a label, not documentation. */
	content: ReactNode;
	children: ReactNode;
	side?: "top" | "right" | "bottom" | "left";
	/**
	 * Forces the tooltip shut, for a control that needs one only sometimes.
	 *
	 * The alternative — wrapping the control in a tooltip in one state and not
	 * in the other — changes the shape of the tree, so React unmounts the
	 * control and mounts a new one. Anybody who had tabbed to it loses their
	 * place, a switch mid-animation restarts, and the node identity changes
	 * under any code holding a reference to it. Keeping the wrapper and closing
	 * it costs nothing.
	 */
	open?: boolean | undefined;
}

/**
 * A tooltip.
 *
 * **Never the only way to learn something.** A tooltip does not open on a touch
 * screen and does not exist for somebody navigating by keyboard alone in every
 * browser, so anything that is required to understand a control belongs in a
 * label or a hint. This is for the extra sentence, and for naming an icon-only
 * control that already carries an `aria-label`.
 *
 * 300ms, which is long enough that a pointer crossing the screen does not leave
 * a trail of them and short enough that a deliberate hover feels answered.
 */
export function Tooltip({ content, children, side = "top", open }: TooltipProps) {
	return (
		// Spread rather than passed: `exactOptionalPropertyTypes` will not let an
		// `undefined` through a prop whose type does not include it, and Radix
		// reads a present `open` as "controlled".
		<RadixTooltip.Root delayDuration={300} {...(open === undefined ? {} : { open })}>
			<RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
			<RadixTooltip.Portal>
				<RadixTooltip.Content
					side={side}
					sideOffset={6}
					className={cx(
						"z-[var(--z-popover)] max-w-56 rounded-md border border-border bg-bg-raised px-2 py-1",
						"text-xs leading-relaxed text-fg shadow-overlay",
					)}
				>
					{content}
					<RadixTooltip.Arrow className="fill-bg-raised" />
				</RadixTooltip.Content>
			</RadixTooltip.Portal>
		</RadixTooltip.Root>
	);
}

/**
 * Wraps the app once so tooltips share a timer.
 *
 * Without it each tooltip waits its own 300ms, so moving along a toolbar pauses
 * before every one. With it, the first wait is 300ms and the rest open at once
 * — which is what a person scanning a row of icons actually wants.
 */
export const TooltipProvider = RadixTooltip.Provider;

/** Text for assistive technology only. */
export const VisuallyHidden = RadixVisuallyHidden.Root;

export interface KbdProps {
	children: ReactNode;
	className?: string;
}

/**
 * A key cap: `⌘K`, `Esc`.
 *
 * A `<kbd>` element, so it is announced as keyboard input rather than as a
 * stray letter. Monospace and tabular, because these sit in a row beside menu
 * items and a proportional font makes them ragged.
 */
export function Kbd({ children, className }: KbdProps) {
	return (
		<kbd
			className={cx(
				"inline-flex h-5 min-w-5 items-center justify-center rounded-[4px] border border-border",
				"bg-bg-subtle px-1 font-mono text-[11px] text-fg-muted",
				className,
			)}
		>
			{children}
		</kbd>
	);
}
